import reactUpdate from 'immutability-helper';
import React from 'react';
import _ from 'underscore';

import {assert} from 'Asserts';
import {Collaborator, isActorCollaborator, Owner, OwnerInfo, PolicyType} from 'auth/PermissionConstants';
import {
  getCollaboratorAdminPolicyId,
  getCollaboratorMemberPolicyId,
  setCollaboratorAdminPolicyId,
  setCollaboratorMemberPolicyId,
  setCollaboratorPolicyId,
} from 'auth/PermissionUtil';
import {Policy} from 'auth/PolicyConstants';
import BenchlingConfig from 'BenchlingConfig';
import AddCollaboratorFormGroup from 'collaborator-components/AddCollaborator';
import CollaboratorsFormGroup, {
  CreatorRowViewPropsGroup,
} from 'collaborator-components/CollaboratorsFormGroup';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import EntityStore from 'stores/EntityStore';
import getTestProps from 'test/lib/cypress/getTestProps';
import {InputChangeEvent} from 'util/Types';

export interface UpdateArgs {
  collaborators?: Array<Collaborator>;
  ownerInfo?: OwnerInfo;
  owner?: Owner;
}

export interface CollaboratorsSectionProps<T extends Policy> {
  getAdminPolicyId: (policies: Array<T>) => string;
  getDefaultPolicyId: (policies: Array<T>) => string;
  collaborators: Array<Collaborator>;
  policies: Array<T>;
  labelText?: string;
  creatorRowPropsGroup?: CreatorRowViewPropsGroup;
  renderDescription?: () => JSX.Element;
  enableAuditors: boolean;
  isReadOnly: boolean;
  onLeave?: (partyId: string) => void;
  onUpdate: (updateArgs: UpdateArgs) => void;
  policeableId?: string | null;
  owner?: Owner;
  ownerInfo?: OwnerInfo;
  policyType: PolicyType;
}

export default class CollaboratorsSection<T extends Policy> extends React.Component<
  CollaboratorsSectionProps<T>
> {
  static defaultProps = {
    enableAuditors: false,
  };

  _handleChangeOwnerDataPolicy = (memberDataPolicyId: string): void => {
    assert(
      this.props.owner !== undefined && this.props.ownerInfo !== undefined,
      'Owner and ownerInfo must be defined.'
    );

    assert(
      this.props.ownerInfo.type === ModelTypeConstants.ORGANIZATION,
      'Owner must be organization to change owner permission.'
    );

    const newState: UpdateArgs = {
      ownerInfo: reactUpdate(this.props.ownerInfo, {$merge: {memberDataPolicyId}}),
    };

    // If the current user isn't a collaborator, add them as an ADMIN collaborator.
    const currentUser = EntityStore.currentUser();
    // eslint-disable-next-line no-restricted-syntax
    const isCurrentUserCollaborator = _.some(
      this.props.collaborators,
      (collaborator) => collaborator.id === currentUser.id
    );
    if (!isCurrentUserCollaborator) {
      newState.collaborators = reactUpdate(this.props.collaborators, {
        $push: [
          {
            dataPolicyId: this.props.getAdminPolicyId(this.props.policies),
            ...currentUser,
          },
        ],
      });
    }
    this.props.onUpdate(newState);
  };

  _handleChangeCollaboratorPolicy = (
    id: string,
    data: {
      memberDataPolicyId?: string | null;
      adminDataPolicyId?: string | null;
      dataPolicyId?: string;
      memberSchemaPolicyId?: string | null;
      adminSchemaPolicyId?: string | null;
      schemaPolicyId?: string;
    }
  ): void => {
    // Duplicate the collaborators, but change the permission of the collaborator specified by `id`
    this.props.onUpdate({
      collaborators: this.props.collaborators.map((c) => {
        if (c.id !== id) {
          return c;
        }
        const collaborator = reactUpdate(c, {$merge: data});
        if (isActorCollaborator(collaborator)) {
          return collaborator;
        }
        if (!getCollaboratorMemberPolicyId(collaborator)) {
          // If setting NONE for member data policy, change admin to default collaborator policy (admins
          // having NONE would be the same as not having a collaborator - so NONE is prohibited for admins).
          setCollaboratorAdminPolicyId(
            collaborator,
            this.props.policyType,
            getCollaboratorAdminPolicyId(collaborator) || this.props.getDefaultPolicyId(this.props.policies)
          );
        }
        return collaborator;
      }),
    });
  };

  _handleAddPartyAsCollaborator = (party: Collaborator): boolean => {
    // No-op if collaborator already present
    if (this.props.collaborators.some((c) => c.id === party.id)) {
      return false;
    }

    // No-op if they are the owner
    if (this.props.owner && this.props.owner?.id === party.id) {
      return false;
    }

    const collaborator = {...party};
    if (isActorCollaborator(collaborator)) {
      setCollaboratorPolicyId(
        collaborator,
        this.props.policyType,
        this.props.getDefaultPolicyId(this.props.policies)
      );
    } else {
      const adminPolicyId =
        collaborator.type === ModelTypeConstants.TEAM &&
        !BenchlingConfig.POLICIES_DEFAULT_TEAM_ADMINS_TO_COLLABORATOR
          ? this.props.getAdminPolicyId(this.props.policies)
          : null;
      setCollaboratorAdminPolicyId(collaborator, this.props.policyType, adminPolicyId);
      setCollaboratorMemberPolicyId(
        collaborator,
        this.props.policyType,
        this.props.getDefaultPolicyId(this.props.policies)
      );
    }
    this.props.onUpdate({
      collaborators: reactUpdate(this.props.collaborators, {$push: [collaborator]}),
    });
    return true;
  };

  _handleRemoveCollaborator = (id: string): void => {
    this.props.onUpdate({
      collaborators: this.props.collaborators.filter((c) => c.id !== id),
    });
  };

  renderAuditorColumnHeader = (): JSX.Element => <th>Auditor</th>;

  _handleChangeCollaboratorAuditorStatus = (id: string, e: InputChangeEvent): void => {
    const auditor = e.target.checked;
    this.props.onUpdate({
      collaborators: this.props.collaborators.map((c) => {
        if (c.id !== id) {
          return c;
        }
        return reactUpdate(c, {$merge: {auditor}});
      }),
    });
  };

  renderAuditorColumn = (id: string): JSX.Element => {
    // Do not render if it is the row of a non-user/non-team
    const isOwner = this.props.owner ? id === this.props.owner?.id : false;
    const collaborator: Collaborator = _.findWhere(this.props.collaborators, {id})!;
    if (isOwner && this.props.ownerInfo && this.props.ownerInfo.type !== ModelTypeConstants.USER) {
      return <td />;
    }
    if (
      !isOwner &&
      [ModelTypeConstants.USER, ModelTypeConstants.TEAM].indexOf(collaborator.type as ModelTypeConstants) ===
        -1
    ) {
      return <td />;
    }

    const isAuditor =
      isOwner || this.props.collaborators.some((c: Collaborator) => Boolean(id === c.id && c.auditor));
    // Cannot set auditor on team if members have no permission.
    const hasTeamNoMemberPolicy =
      !isOwner &&
      collaborator.type === ModelTypeConstants.TEAM &&
      !getCollaboratorMemberPolicyId(collaborator);
    const onChange = this._handleChangeCollaboratorAuditorStatus.bind(this, id);
    return (
      <td>
        <input
          type="checkbox"
          checked={isAuditor}
          onChange={onChange}
          disabled={isOwner || this.props.isReadOnly || hasTeamNoMemberPolicy}
        />
      </td>
    );
  };

  override render(): JSX.Element {
    const existingCollaboratorIds = this.props.collaborators.map((c) => c.id);
    if (this.props.owner) {
      existingCollaboratorIds.push(this.props.owner.id);
    }
    return (
      <div className="well" {...getTestProps({component: 'CollaboratorsSection', element: 'root'})}>
        {!this.props.isReadOnly && (
          <AddCollaboratorFormGroup
            addPartyAsCollaborator={this._handleAddPartyAsCollaborator}
            existingCollaboratorIds={existingCollaboratorIds}
            ownableId={this.props.policeableId}
            labelText={this.props.labelText}
            allowRoutineAgents={BenchlingConfig.LLM_ENABLE_ROUTINES_RUN_AS_SERVICE_ACCOUNT}
          />
        )}
        {this.props.renderDescription && this.props.renderDescription()}
        <CollaboratorsFormGroup
          owner={this.props.owner}
          ownerInfo={this.props.ownerInfo}
          onChangeOwnerPolicy={this._handleChangeOwnerDataPolicy}
          onChangeCollaboratorPolicy={this._handleChangeCollaboratorPolicy}
          onLeave={this.props.onLeave}
          onRemoveCollaborator={this._handleRemoveCollaborator}
          policies={this.props.policies}
          creatorRowPropsGroup={this.props.creatorRowPropsGroup}
          isReadOnly={this.props.isReadOnly}
          collaborators={this.props.collaborators}
          renderExtraColumns={this.props.enableAuditors ? this.renderAuditorColumn : null}
          renderExtraColumnHeaders={this.props.enableAuditors ? this.renderAuditorColumnHeader : null}
        />
      </div>
    );
  }
}
